'use client';

import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import InputFloatingLabel from '@/components/forms/InputFloatingLabel';
import { LiaMapMarkedAltSolid, LiaSearchSolid } from 'react-icons/lia';
import { debounce } from 'lodash';
import {
  Alert,
  Autocomplete,
  AutocompleteItem,
  Button,
  NumberInput,
  Spinner,
  Tooltip,
  addToast,
} from '@heroui/react';
import {
  callMapboxPlaces,
  geocodeAddress,
} from '@/util/functions/fetching/callMapboxPlaces';
import { useForm, Controller } from 'react-hook-form';
import {
  AddressForScheduler,
  FOUNDATION_OPTIONS,
} from '@/util/types/globalTypes';
import { toPropertyNumber } from '@/util/functions/property/toPropertyNumber';
import {
  findSelectedSuggestion,
  geocodePropertyAddress,
  suggestionLabel,
  suggestionPrecision,
} from '@/util/functions/mapbox/addressSuggestion';
import { isApproximateLocation } from '@attik/types/property.types';
import { IoWarningOutline } from 'react-icons/io5';

/** Lets a host that saves with its own button (Edit Property) save what is on screen. */
export type AddressFormHandle = {
  /**
   * Saves the values currently in the form, cancelling a pending auto-save and
   * superseding one already in flight. Resolves with the saved address, or
   * `null` when the address could not be validated (staff are told why).
   */
  flush: () => Promise<AddressForScheduler | null>;
};

/** Lookup-filled fields whose "needs verification" flag clears once staff edit them. */
const VERIFIED_FLAG_BY_FIELD = {
  sqft: 'sqftVerified',
  yearBuilt: 'yearBuiltVerified',
  unitCount: 'unitCountVerified',
  foundation: 'foundationVerified',
} as const satisfies Partial<
  Record<keyof AddressForScheduler, keyof AddressForScheduler>
>;

type VerifiableField = keyof typeof VERIFIED_FLAG_BY_FIELD;

const isVerifiableField = (name: string): name is VerifiableField =>
  Object.hasOwn(VERIFIED_FLAG_BY_FIELD, name);

/** Blank and missing are the same unit/zip; only a real difference re-geocodes. */
const sameText = (a?: string | null, b?: string | null) =>
  (a ?? '') === (b ?? '');

type MapboxContext = { id: string; text: string };

type AddressAutocompleteItem = {
  place_name: string;
  matching_place_name?: string;
  address?: string;
  center?: [number, number];
  context?: MapboxContext[];
  isEmptyState?: boolean;
};

// Helper function to abbreviate street types
const abbreviateStreetType = (address: string): string => {
  const streetTypeAbbreviations: { [key: string]: string } = {
    Street: 'St',
    Avenue: 'Ave',
    Boulevard: 'Blvd',
    Drive: 'Dr',
    Lane: 'Ln',
    Court: 'Ct',
    Place: 'Pl',
    Circle: 'Cir',
    Parkway: 'Pkwy',
    Highway: 'Hwy',
    Terrace: 'Ter',
    Square: 'Sq',
    Trail: 'Trl',
    Way: 'Way',
    North: 'N',
    South: 'S',
    East: 'E',
    West: 'W',
  };

  let result = address;

  // Replace street types (case insensitive)
  Object.entries(streetTypeAbbreviations).forEach(([full, abbrev]) => {
    const regex = new RegExp(`\\b${full}\\b`, 'gi');
    result = result.replace(regex, abbrev);
  });

  return result;
};

export default function AddressSearchInputFallbackForm({
  autoConvertToForm = false,
  address,
  setAddress,
  canRestart = true,
  hidePropertyDetails = false,
  accessToken,
  disableAutoSave = false,
  onValidationChange,
  proximity,
  formHandleRef,
  showLocationPrecisionAlert = true,
}: {
  autoConvertToForm?: boolean;
  address?: AddressForScheduler | null;
  setAddress:
    | React.Dispatch<React.SetStateAction<AddressForScheduler | null>>
    | ((address: AddressForScheduler | null) => void);
  canRestart?: boolean;
  hidePropertyDetails?: boolean;
  accessToken?: string;
  disableAutoSave?: boolean;
  onValidationChange?: (isValidating: boolean) => void;
  proximity?: { lat: number; lng: number };
  /** Set while the address form is shown; `null` while searching. */
  formHandleRef?: React.Ref<AddressFormHandle>;
  /** Off when the host shows the approximate-location warning itself. */
  showLocationPrecisionAlert?: boolean;
}) {
  const [searchedAddress, setsearchedAddress] = useState<string>('');
  const [autoArray, setAutoArray] = useState<AddressAutocompleteItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleAddressChange = async (value: string) => {
    // strip out "#" and "/" from the value
    const strippedValue = value.replace(/[#/]/g, '');

    const features = await callMapboxPlaces(strippedValue, {
      accessToken,
      proximity,
    });
    setAutoArray(features);
    setIsSearching(false);
    setHasSearched(true);
  };

  // console.log({ autoArray, address });

  useEffect(() => {
    const debouncedHandleAddressChange = debounce(async (value: string) => {
      await handleAddressChange(value);
    }, 1000);

    if (isCompleted) return setIsCompleted(false);
    if (searchedAddress.length > 6) {
      setIsSearching(true);

      debouncedHandleAddressChange(searchedAddress);
    } else {
      setAutoArray([]);
    }

    return () => {
      debouncedHandleAddressChange.cancel();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedAddress]);

  // Hydrate form when address is pre-filled (e.g., from draft)
  useEffect(() => {
    if (address && address.street) {
      setIsCompleted(true);
      setHasSearched(true);
      if (address.address) {
        setsearchedAddress(address.address);
      }
    }
  }, [address]);

  const handleAutoCompleteClick = async (item: string | null) => {
    // return console.log(' inthe handleAutoCompleteClick', item);
    if (!item) return;
    const foundAdd = findSelectedSuggestion(autoArray, item);
    if (!foundAdd?.context || !foundAdd.center)
      return console.log('no address found');

    const finalAddress = suggestionLabel(foundAdd);
    let address = {
      address: finalAddress,
      street: abbreviateStreetType(finalAddress.split(',')[0]),
      city: foundAdd.context.find((data) => data.id.includes('place'))!.text,
      state: foundAdd.context.find((data) => data.id.includes('region'))!.text,
      zip: foundAdd.context.find((data) => data.id.includes('postcode'))!.text,
      lat: foundAdd.center[1],
      lng: foundAdd.center[0],
      locationPrecision: suggestionPrecision(foundAdd),
    };
    setIsCompleted(true);
    setAddress(address);
    setsearchedAddress(finalAddress);
    setAutoArray([]);
  };

  // console.log({ autoArray });

  return (
    <div className='relative'>
      {((!address && !isManualEntry) ||
        (!autoConvertToForm && !isManualEntry)) && (
        <>
          <Autocomplete
            items={
              autoArray.length === 0 &&
              hasSearched &&
              searchedAddress.length > 6
                ? [{ place_name: '__no_results__', isEmptyState: true }]
                : autoArray.length === 0
                  ? []
                  : autoArray
            }
            label='Search Address'
            placeholder='Enter an address...'
            startContent={<LiaMapMarkedAltSolid size={23} />}
            endContent={isSearching ? <Spinner size='sm' /> : null}
            inputValue={searchedAddress}
            selectedKey={address ? address.address : null}
            allowsCustomValue={true}
            allowsEmptyCollection={true}
            menuTrigger='input'
            onInputChange={(value) => {
              setsearchedAddress(value);
              setHasSearched(false);
            }}
            onSelectionChange={(key) => {
              if (key && key !== '__no_results__') {
                handleAutoCompleteClick(key.toString());
              }
            }}
            inputProps={{
              classNames: {
                inputWrapper:
                  'border max-w-full bg-white overflow-clip shadow-none',
                label: 'font-light uppercase text-xs',
              },
            }}
            variant='bordered'
            // onClear={() => {
            //   setsearchedAddress('');
            //   setAddress(null);
            //   setAutoArray([]);
            //   setHasSearched(false);
            // }}
            // isClearable
            selectorIcon={null}
          >
            {(item: AddressAutocompleteItem) =>
              item.isEmptyState ? (
                <AutocompleteItem
                  key='__no_results__'
                  textValue='No results'
                  className='cursor-default'
                >
                  <div className='p-4 text-center text-gray-500'>
                    <div className='flex flex-col items-center gap-2'>
                      <LiaSearchSolid size={24} className='opacity-50' />
                      <p className='text-sm'>No addresses found</p>
                      <p className='text-xs'>
                        Try a different search term or enter manually
                      </p>
                      <Button
                        as='div'
                        size='sm'
                        variant='flat'
                        color='primary'
                        className='mt-2'
                        onPress={() => {
                          setIsManualEntry(true);
                          setAutoArray([]);
                          setHasSearched(false);
                        }}
                      >
                        Enter Address Manually
                      </Button>
                    </div>
                  </div>
                </AutocompleteItem>
              ) : (
                <AutocompleteItem
                  key={suggestionLabel(item)}
                  textValue={suggestionLabel(item)}
                >
                  {suggestionLabel(item)}
                </AutocompleteItem>
              )
            }
          </Autocomplete>

          <div className='mt-2 flex justify-end'>
            <button
              className='link-primary text-xs'
              type='button'
              onClick={() => setIsManualEntry(true)}
            >
              Enter Address Manually
            </button>
          </div>
        </>
      )}
      {((address && autoConvertToForm) || isManualEntry) && (
        <>
          <AddressForm
            setsearchedAddress={setsearchedAddress}
            setIsManualEntry={setIsManualEntry}
            address={address}
            setAddress={setAddress}
            canRestart={canRestart}
            hidePropertyDetails={hidePropertyDetails}
            accessToken={accessToken}
            disableAutoSave={disableAutoSave}
            onValidationChange={onValidationChange}
            proximity={proximity}
            formHandleRef={formHandleRef}
            showLocationPrecisionAlert={showLocationPrecisionAlert}
          />
        </>
      )}
    </div>
  );
}

function AddressForm({
  setsearchedAddress,
  setIsManualEntry,
  address,
  setAddress,
  canRestart = true,
  hidePropertyDetails = false,
  accessToken,
  disableAutoSave = false,
  onValidationChange,
  proximity,
  formHandleRef,
  showLocationPrecisionAlert = true,
}: {
  setsearchedAddress: React.Dispatch<React.SetStateAction<string>>;
  setIsManualEntry: React.Dispatch<React.SetStateAction<boolean>>;
  address?: AddressForScheduler | null;
  setAddress:
    | React.Dispatch<React.SetStateAction<AddressForScheduler | null>>
    | ((address: AddressForScheduler | null) => void);
  canRestart?: boolean;
  hidePropertyDetails?: boolean;
  accessToken?: string;
  disableAutoSave?: boolean;
  onValidationChange?: (isValidating: boolean) => void;
  proximity?: { lat: number; lng: number };
  formHandleRef?: React.Ref<AddressFormHandle>;
  showLocationPrecisionAlert?: boolean;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const defaultBlank: AddressForScheduler = {
    address: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    lat: 0,
    lng: 0,
    sqft: undefined,
    yearBuilt: undefined,
    unitCount: undefined,
    foundation: undefined,
    sqftVerified: true,
    yearBuiltVerified: true,
    unitCountVerified: true,
    foundationVerified: true,
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    watch,
    getValues,
    trigger,
    formState: { dirtyFields, errors },
  } = useForm<AddressForScheduler>({
    defaultValues: address || defaultBlank,
    mode: 'onChange',
  });

  // Register foundation field with validation
  useEffect(() => {
    register('foundation', {
      required: 'Foundation is required',
    });
  }, [register]);

  /**
   * Staff edits are taken from the inputs' own events, never from a `watch()`
   * subscription. Since react-hook-form 7.55 a watch callback is replayed with
   * the last keystroke's `type: 'change'` whenever a watched field is set,
   * including by `reset()`. Marking a field verified from that callback set
   * another watched field, which replayed it again, until the stack overflowed
   * on the first reload after a manual address was geocoded. An
   * auto-save or reset therefore never counts as an edit and never schedules
   * another save.
   *
   * `editVersionRef` counts edits so a save can tell when staff kept typing
   * while it waited on the geocoder; `saveSeqRef` lets the newest save win.
   */
  const editVersionRef = useRef(0);
  const saveSeqRef = useRef(0);

  const setValidating = (validating: boolean) => {
    setIsValidating(validating);
    onValidationChange?.(validating);
  };

  // Use shorter debounce when disableAutoSave is true (no toasts shown, so faster response is better)
  const autoSaveDelay = disableAutoSave ? 500 : 5000;
  const autoSaveRef = useRef<() => void>(() => {});
  const debouncedAutoSave = useMemo(
    () => debounce(() => autoSaveRef.current(), autoSaveDelay),
    [autoSaveDelay]
  );

  const handleStaffEdit = (fieldName?: string) => {
    if (fieldName && isVerifiableField(fieldName)) {
      setValue(VERIFIED_FLAG_BY_FIELD[fieldName], true, { shouldDirty: false });
    }
    editVersionRef.current += 1;
    setValidating(true);
    debouncedAutoSave();
  };

  useEffect(() => () => debouncedAutoSave.cancel(), [debouncedAutoSave]);

  const onValidationChangeRef = useRef(onValidationChange);
  useEffect(() => {
    onValidationChangeRef.current = onValidationChange;
  });

  useEffect(() => {
    const saveSeq = saveSeqRef;
    const notifyValidation = onValidationChangeRef;
    return () => {
      // Back To Search unmounts the form: drop any save still in flight so it
      // cannot restore the address staff just cleared, and stop the host
      // waiting on a validation that will never report.
      saveSeq.current += 1;
      notifyValidation.current?.(false);
    };
  }, []);

  // The host can replace the address at any time, e.g. the new order page
  // filling in property details after a save. Fields staff edited since the
  // last save keep what they typed; the pending auto-save then stores both.
  useEffect(() => {
    if (address) {
      reset(address, { keepDirtyValues: true });
    }
  }, [address, reset]);

  /**
   * Year Built and # Of Units render through `InputFloatingLabel`, which wraps
   * the input in a react-hook-form `Controller`; HeroUI's `Input` hands that
   * `onChange` a `ChangeEvent`, so the form stores `e.target.value` — a string —
   * and the `valueAsNumber` passed to `register()` never runs (it is a
   * register-only option, and the Controller's `onChange` wins).
   *
   * A stringified Year Built then failed the numeric check behind the property
   * review step, so a customer who had entered one was told it was still
   * required and could not continue. Coerce on the way out, which is the one
   * path every save takes, so context, drafts and the backend only ever see
   * numbers regardless of which control wrote the value.
   */
  const withNumericPropertyFields = (
    data: AddressForScheduler
  ): AddressForScheduler => ({
    ...data,
    sqft: toPropertyNumber(data.sqft),
    yearBuilt: toPropertyNumber(data.yearBuilt),
    unitCount: toPropertyNumber(data.unitCount),
  });

  const submitForm = async (
    rawData: AddressForScheduler,
    isAutoSave = false
  ): Promise<AddressForScheduler | null> => {
    const saveSeq = ++saveSeqRef.current;
    const editVersion = editVersionRef.current;
    // A newer save, or staff typing while this one waited on the geocoder,
    // makes this result stale: resetting the form to it would wipe what they
    // typed since. The newer save, or the auto-save their typing scheduled,
    // reports instead.
    const isSuperseded = () =>
      saveSeq !== saveSeqRef.current ||
      editVersion !== editVersionRef.current;

    if (!isAutoSave) setIsLoading(true);
    setValidating(true);

    const data = withNumericPropertyFields(rawData);

    try {
      // Check if address already has valid coordinates
      const hasValidCoordinates =
        address?.lat && address?.lng && address.lat !== 0 && address.lng !== 0;

      // The pin belongs to street, city, state and zip. A unit change alone keeps it.
      const locationChanged =
        !hasValidCoordinates ||
        address?.street !== data.street ||
        address?.city !== data.city ||
        address?.state !== data.state ||
        !sameText(address?.zip, data.zip);

      // Only geocode if we don't have valid coordinates or if address fields changed
      const addressFieldsChanged =
        locationChanged || !sameText(address?.unit, data.unit);

      let location: Pick<
        AddressForScheduler,
        'lat' | 'lng' | 'locationPrecision'
      > | null = null;

      if (addressFieldsChanged) {
        location = await geocodePropertyAddress(data, (query, attempt) =>
          geocodeAddress(query, {
            showErrorToasts: false,
            silentMode: true,
            accessToken,
            proximity,
            propertyData: data,
            ...attempt,
          })
        );

        if (isSuperseded()) return null;

        // Only a unit change can fall back to the pin already saved. When the
        // street, city, state or zip changed, that pin belongs to the old address,
        // and keeping it is exactly how a job's map drifts from its address.
        if (!location && !locationChanged && address) {
          location = {
            lat: address.lat,
            lng: address.lng,
            locationPrecision: address.locationPrecision,
          };
        }

        // If all geocoding attempts failed, refuse rather than save a wrong pin
        if (!location) {
          const errorMessage = data.zip
            ? `Unable to find coordinates for this address. Please verify the zipcode "${data.zip}" is valid.`
            : 'Unable to find coordinates for this address. Please ensure all address fields are correct.';

          if (!isAutoSave || disableAutoSave) {
            addToast({
              title: 'Address geocoding failed',
              description: errorMessage,
              color: 'danger',
            });
          }
          throw new Error(errorMessage);
        }
      } else {
        // Address fields haven't changed, use existing coordinates
        location = {
          lat: address.lat,
          lng: address.lng,
          locationPrecision: address.locationPrecision,
        };
      }

      const newAddress = {
        ...data,
        address: `${data.street}${data.unit ? ` ${data.unit}` : ''}, ${
          data.city
        }, ${data.state} ${data.zip}`,
        lat: location.lat,
        lng: location.lng,
        locationPrecision: location.locationPrecision,
      };

      setAddress(newAddress);
      reset(newAddress);
      setValidating(false);

      if (isAutoSave && !disableAutoSave) {
        addToast({
          title: 'Address auto-saved',
          description: 'Your changes have been automatically saved',
          color: 'success',
          timeout: 2000,
        });
      }
      return newAddress;
    } catch (error) {
      if (isSuperseded()) return null;
      console.error('Error saving address:', error);
      setValidating(false);

      // Don't save address if geocoding failed - this prevents errors throughout the system
      if (!isAutoSave || disableAutoSave) {
        // Error toast already shown above if it was a geocoding error
        if (
          !(
            error instanceof Error &&
            error.message.includes('Unable to find coordinates')
          )
        ) {
          addToast({
            title: 'Error saving address',
            description: 'Please try again',
            color: 'danger',
          });
        }
      }
      return null;
    } finally {
      if (!isAutoSave) setIsLoading(false);
    }
  };

  useEffect(() => {
    autoSaveRef.current = () => void submitForm(getValues(), true);
  });

  const flush = () => {
    debouncedAutoSave.cancel();
    return submitForm(getValues(), false);
  };

  useImperativeHandle(formHandleRef, () => ({ flush }));

  const onSubmit = async (data: AddressForScheduler) => {
    await submitForm(data, false);
  };

  return (
    <div className=''>
      <form
        className='grid grid-cols-6 gap-x-1'
        onSubmit={handleSubmit(onSubmit)}
        // Typing in a field bubbles here; resets and setValue never do. Only
        // named inputs hold form values (the Foundation search text does not).
        onChange={(e) => {
          if (e.target instanceof HTMLInputElement && e.target.name) {
            handleStaffEdit(e.target.name);
          }
        }}
      >
        <div className='col-span-4'>
          <InputFloatingLabel
            register={register('street', {
              required: { value: true, message: 'Street is required' },
            })}
            label='Street'
            size='sm'
            error={errors.street?.message}
          />
        </div>
        <div className='col-span-2'>
          <InputFloatingLabel
            register={register('unit')}
            label='Unit'
            size='sm'
          />
        </div>
        <div className='col-span-2'>
          <InputFloatingLabel
            register={register('city', {
              required: { value: true, message: 'City is required' },
            })}
            label='City'
            size='sm'
            error={errors.city?.message}
          />
        </div>
        <div className='col-span-2'>
          <InputFloatingLabel
            register={register('state', {
              required: { value: true, message: 'State is required' },
            })}
            label='State'
            size='sm'
            error={errors.state?.message}
          />
        </div>
        <div className='col-span-2'>
          <InputFloatingLabel
            register={register('zip', {
              required: { value: true, message: 'Zip is required' },
            })}
            label='Zip'
            size='sm'
            error={errors.zip?.message}
          />
        </div>
        {showLocationPrecisionAlert &&
          isApproximateLocation(address?.locationPrecision) && (
          <div className='col-span-6 mb-2'>
            <Alert
              color='warning'
              variant='flat'
              title='Approximate location'
              description={
                address?.locationPrecision === 'postcode'
                  ? 'This address could not be found, so the map pin is the centre of the zip code. Drive times and directions may be off — check the street and zip, or pick the address from the search.'
                  : 'This street address could not be found, so the map pin is a nearby address in the same zip. Drive times and directions may be off — check the street, or pick the address from the search.'
              }
            />
          </div>
        )}

        {!hidePropertyDetails && (
          <>
            <div className='col-span-6 flex items-center justify-between gap-2'>
              <hr className='grow' />
              <p className='text-xxs font-light uppercase text-gray-500'>
                Property Details
              </p>
              <hr className='grow' />
            </div>
            <div className='col-span-3'>
              <Controller
                name='sqft'
                control={control}
                render={({ field }) => (
                  <NumberInput
                    label='SQFT'
                    size='sm'
                    variant='bordered'
                    isRequired
                    min={0}
                    step={1}
                    formatOptions={{
                      maximumFractionDigits: 0,
                      minimumFractionDigits: 0,
                    }}
                    value={field.value ?? undefined}
                    onValueChange={(value) => {
                      // A cleared field is stored as NaN, not undefined: a
                      // Controller showing `undefined` falls back to the
                      // default value, so the old SQFT would reappear and the
                      // next keystroke append to it. `toPropertyNumber` saves
                      // NaN as blank.
                      const next =
                        value === undefined || Number.isNaN(value)
                          ? NaN
                          : Math.trunc(value);
                      field.onChange(next);
                      // Also covers the stepper buttons, which fire no input event.
                      handleStaffEdit('sqft');
                    }}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                    classNames={{
                      inputWrapper: ['bg-white', 'border', 'shadow-none'],
                      label: 'font-light uppercase text-xs',
                      input: 'text-base md:text-sm',
                      base: 'py-1'
                    }}
                    endContent={
                      !watch('sqftVerified') && (
                        <Tooltip
                          delay={400}
                          content='This data was automatically populated and needs verification'
                          color='warning'
                          closeDelay={0}
                          className='max-w-40 text-center'
                        >
                          <IoWarningOutline className='text-xl text-warning' />
                        </Tooltip>
                      )
                    }
                  />
                )}
              />
            </div>
            <div className='col-span-3'>
              <InputFloatingLabel
                type='number'
                register={register('yearBuilt', { valueAsNumber: true })}
                control={control}
                controlName='yearBuilt'
                label='Year Built'
                size='sm'
                isRequired
                endContent={
                  !watch('yearBuiltVerified') && (
                    <Tooltip
                      content='This data was automatically populated and needs verification'
                      color='warning'
                      delay={400}
                      className='max-w-40 text-center'
                      closeDelay={0}
                    >
                      <IoWarningOutline className='text-xl text-warning' />
                    </Tooltip>
                  )
                }
              />
            </div>
            <div className='col-span-3'>
              <InputFloatingLabel
                type='number'
                register={register('unitCount', { valueAsNumber: true })}
                control={control}
                controlName='unitCount'
                label='# Of Units'
                size='sm'
                endContent={
                  !watch('unitCountVerified') && (
                    <Tooltip
                      content='This data was automatically populated and needs verification'
                      color='warning'
                      delay={400}
                      closeDelay={0}
                      className='max-w-40 text-center'
                    >
                      <IoWarningOutline className='text-xl text-warning' />
                    </Tooltip>
                  )
                }
              />
            </div>
            <div className='col-span-3'>
              <Autocomplete
                items={FOUNDATION_OPTIONS.map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                }))}
                label='Foundation'
                placeholder='Select foundation type...'
                selectedKey={watch('foundation') || null}
                isRequired
                isInvalid={!!errors.foundation}
                errorMessage={errors.foundation?.message}
                onSelectionChange={async (key) => {
                  setValue('foundation', key ? key.toString() : undefined, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  handleStaffEdit('foundation');
                  await trigger('foundation');
                }}
                inputProps={{
                  classNames: {
                    inputWrapper:
                      'border max-w-full bg-white overflow-clip shadow-none',
                    label: 'font-light uppercase text-xs',
                  },
                }}
                variant='bordered'
                size='sm'
                className='py-1'
                endContent={
                  !watch('foundationVerified') && (
                    <Tooltip
                      content='This data was automatically populated and needs verification'
                      color='warning'
                      delay={400}
                      closeDelay={0}
                      className='max-w-40 text-center'
                    >
                      <IoWarningOutline className='text-xl text-warning' />
                    </Tooltip>
                  )
                }
              >
                {(item: { value: string; label: string }) => (
                  <AutocompleteItem key={item.value} textValue={item.label}>
                    {item.label}
                  </AutocompleteItem>
                )}
              </Autocomplete>
            </div>
          </>
        )}
        <div className='col-span-6 flex items-center justify-between'>
          {canRestart && (
            <button
              className='link-primary text-xs'
              type='button'
              onClick={() => {
                setAddress(null);
                setsearchedAddress('');
                setIsManualEntry(false);
              }}
            >
              {'<-'} Back To Search
            </button>
          )}
          <div className='min-h-6 flex items-center gap-2 text-xs text-gray-600'>
            {isValidating && (
              <>
                <Spinner size='sm' color='primary' />
                <span>Validating address...</span>
              </>
            )}
          </div>
        </div>
        {!disableAutoSave && (
          <div className='col-span-6 mt-4'>
            {Object.keys(dirtyFields).length > 0 && (
              <Button
                // className='flex items-center gap-1 rounded border border-primary bg-white px-2 text-sm font-bold uppercase text-primary hover:bg-primary/10'
                type='submit'
                color='primary'
                // size='sm'
                className='w-full font-bold uppercase'
                isLoading={isLoading}
                disabled={isLoading}
              >
                {!address?.lat ? (
                  <>
                    <LiaSearchSolid className='inline' size={14} /> Search
                  </>
                ) : (
                  `Update Property`
                )}
              </Button>
            )}
          </div>
        )}
      </form>
    </div>
  );
}

/*
 <Autocomplete
        items={autoArray}
        label='Search Address'
        startContent={<LiaMapMarkedAltSolid size={23} />}
        endContent={isSearching ? <Spinner size='sm' /> : null}
        inputValue={searchedAddress}
        // errorMessage={err?.message}
        // isInvalid={!!err}
        // disabled={disabled}
        selectedKey={address ? address.address : null}
        onInputChange={(e) => {
          console.log({ e });
          setsearchedAddress(e);
          //   debouncedHandleAddressChange(e.target.value);
          // field.onChange(e.target.value);
        }}
        onSelectionChange={(e) => handleAutoCompleteClick(e)}
        onClear={() => {
          console.log('clearing');
          setsearchedAddress('');
          setAddress(null);
          setAutoArray([]);
        }}
        popoverProps={{
          isOpen: false,
        }}
        allowsEmptyCollection={false}
      >
        {(x) => (
          <AutocompleteItem
            key={x.place_name}
            // onClick={() => handleAutoCompleteClick(autoArray.indexOf(x))}
          >
            {x.place_name}
          </AutocompleteItem>
        )}
      </Autocomplete>

  
      {autoArray.length > 0 && (
        <div className='dropdown dropdown-open w-full'>

          <ul className='menu dropdown-content z-[1] w-full rounded-xl border border-themeBlack bg-base-100 p-2 shadow'>
            {autoArray.map((x: any) => (
              <li key={x.place_name} className='w-full overflow-hidden'>
                <button
                  className='w-full overflow-hidden'
                  type='button'
                  key={x.place_name}
                  onClick={() => {
                    handleAutoCompleteClick(autoArray.indexOf(x));
                    //   field.onChange(x.place_name);
                  }}
                >
                  {x.place_name}
                </button>
              </li>
            ))}
          </ul>
        </div>
  
*/
